import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import AssetDetailModal from "@/components/AssetDetailModal";
import BunnySnapshotCard from "@/components/BunnySnapshotCard";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, ThumbsUp, ThumbsDown, RotateCcw, MessageSquare, Film, Folder, Link2, FileVideo, ArrowRight, Loader2, CheckCircle2, Copy, Check, PackageCheck } from "lucide-react";
import { healAssetThumbnail } from "@/lib/healThumbnail";

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtTs(sec) {
  if (sec === null || sec === undefined || isNaN(sec) || sec < 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ProjectDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("wd_view") || "grid");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data));
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  const reload = () => api.get("/dashboard/stats").then((r) => setStats(r.data));

  if (!stats) {
    return (
      <div className="flex min-h-screen grain-overlay">
        <Sidebar folders={folders} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
        </div>
      </div>
    );
  }

  const t = stats.tiles;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="flex min-h-screen grain-overlay">
      <Sidebar folders={folders} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          view={view}
          setView={setView}
          onSearch={setSearch}
          searchValue={search}
          onUpload={() => {}}
          canUpload={false}
        />
        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{greet}, {user?.name?.split(" ")[0] || "there"}</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Project status</h1>
            <p className="text-sm text-zinc-500 mt-1">Where every client review currently stands.</p>
          </div>

          {/* Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
            <Tile icon={Clock}        label="Awaiting review"   value={t.awaiting_review}   accent="amber" />
            <Tile icon={ThumbsUp}      label="Approved · 7d"     value={t.approved_week}     accent="emerald" />
            <Tile icon={RotateCcw}     label="Changes requested" value={t.changes_requested} accent="amber" />
            <Tile icon={CheckCircle2}  label="Resolved · 7d"     value={t.resolved_week ?? 0} accent="emerald" />
            <Tile icon={PackageCheck}  label="Delivered · 7d"    value={t.delivered_week ?? 0} accent="emerald" />
            <Tile icon={ThumbsDown}    label="Rejected"          value={t.rejected}          accent="red" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top pending */}
            <section className="lg:col-span-2 bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 sm:p-6 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
                  <FileVideo className="w-4 h-4 text-[var(--brand-accent)]" /> Awaiting your client&rsquo;s review
                </h2>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t.awaiting_review} total</span>
              </div>

              {/* Aging buckets */}
              {stats.aging && (t.awaiting_review > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="aging-buckets">
                  <AgingBucket label="Fresh"  sub="0–3d"   count={stats.aging.fresh}  color="emerald" testid="aging-fresh" />
                  <AgingBucket label="Aging"  sub="4–7d"   count={stats.aging.aging}  color="amber"   testid="aging-aging" />
                  <AgingBucket label="Stale"  sub="8–14d"  count={stats.aging.stale}  color="orange"  testid="aging-stale" />
                  <AgingBucket label="Cold"   sub="15d+"   count={stats.aging.cold}   color="red"     testid="aging-cold" />
                </div>
              )}

              {stats.top_pending.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-500">
                  <ThumbsUp className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  Everything&rsquo;s either approved or has decisions. Nice work. 🎬
                </div>
              ) : (
                <ul className="space-y-2">
                  {stats.top_pending.map((p) => (
                    <PendingRow key={p.id} p={p} onOpen={() => setActive(p.id)} />
                  ))}
                </ul>
              )}
            </section>

            {/* Activity feed */}
            <section className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 sm:p-6 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[var(--brand-accent)]" /> Recent activity
                </h2>
              </div>
              {stats.recent_activity.length === 0 ? (
                <div className="py-8 text-center text-xs text-zinc-500">No activity yet.</div>
              ) : (
                <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                  {stats.recent_activity.map((c, i) => <ActivityItem key={i} c={c} onOpen={() => setActive(c.asset_id)} />)}
                </ul>
              )}
            </section>
          </div>

          {/* Quick links */}
          <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat icon={Film} label="Videos" value={stats.totals.assets} onClick={() => navigate("/library")} />
            <MiniStat icon={Folder} label="Folders" value={stats.totals.folders} onClick={() => navigate("/library")} />
            <MiniStat icon={Link2} label="Active share links" value={stats.totals.shares} onClick={() => navigate("/shares")} />
            <MiniStat icon={MessageSquare} label="Comments" value={stats.totals.comments} onClick={() => {}} />
          </section>

          {/* Bunny encoder snapshot (admin only) */}
          {user?.role === "admin" && (
            <div className="mt-10">
              <BunnySnapshotCard />
            </div>
          )}
        </main>
      </div>

      {active && (
        <AssetDetailModal assetId={active} onClose={() => setActive(null)} onChange={reload} />
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  const colors = {
    amber: "text-[var(--brand-accent)] border-amber-900/40",
    emerald: "text-emerald-400 border-emerald-900/40",
    red: "text-red-400 border-red-900/40",
  };
  return (
    <div className={`bg-zinc-950/50 border ${colors[accent]} rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-4 h-4 ${colors[accent].split(" ")[0]}`} />
        <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      </div>
      <div className={`font-heading text-4xl font-bold tracking-tight ${colors[accent].split(" ")[0]}`}>{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, onClick }) {
  return (
    <button onClick={onClick} className="bg-zinc-950/40 border border-zinc-800/60 hover:border-[var(--brand-accent)]/40 rounded-lg p-4 text-left transition">
      <Icon className="w-4 h-4 text-[var(--brand-accent)] mb-2" />
      <div className="text-2xl font-heading font-semibold text-zinc-100">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
    </button>
  );
}

function ActivityItem({ c, onOpen }) {
  const Icon = c.type === "approval"
    ? ({ approved: ThumbsUp, rejected: ThumbsDown, "changes-requested": RotateCcw }[c.approval] || ThumbsUp)
    : MessageSquare;
  const color = c.type === "approval"
    ? ({ approved: "text-emerald-400", rejected: "text-red-400", "changes-requested": "text-amber-400" }[c.approval] || "text-zinc-400")
    : "text-zinc-400";
  const verb = c.type === "approval"
    ? ({ approved: "approved", rejected: "rejected", "changes-requested": "requested changes on" }[c.approval] || "decided on")
    : "commented on";
  const ts = fmtTs(c.timestamp_sec);

  return (
    <li>
      <button onClick={onOpen} className="w-full text-left group flex items-start gap-2.5">
        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color}`} />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-300">
            <span className="font-medium text-zinc-100">{c.commenter_name}</span>{" "}
            <span className="text-zinc-500">{verb}</span>{" "}
            <span className="font-medium text-zinc-100 group-hover:text-[var(--brand-accent)] transition">{c.asset_title}</span>
            {ts && <span className="text-[var(--brand-accent)] ml-1">[{ts}]</span>}
          </div>
          {c.text && <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{c.text}</div>}
          <div className="text-[10px] text-zinc-600 mt-0.5">{timeAgo(c.created_at)}</div>
        </div>
      </button>
    </li>
  );
}


const BUCKET_COLORS = {
  emerald: { wrap: "border-emerald-900/40 bg-emerald-950/20", text: "text-emerald-400" },
  amber:   { wrap: "border-amber-900/40 bg-amber-950/20",     text: "text-[var(--brand-accent)]" },
  orange:  { wrap: "border-orange-900/40 bg-orange-950/20",   text: "text-orange-400" },
  red:     { wrap: "border-red-900/40 bg-red-950/20",         text: "text-red-400" },
};

function AgingBucket({ label, sub, count, color, testid }) {
  const c = BUCKET_COLORS[color];
  return (
    <div data-testid={testid} className={`border rounded-lg px-3 py-2.5 min-w-0 ${c.wrap}`}>
      <div className="flex items-baseline justify-between gap-1 min-w-0">
        <span className={`text-[10px] uppercase tracking-[0.18em] truncate ${c.text}`}>{label}</span>
        <span className="text-[9px] uppercase tracking-wider text-zinc-500 shrink-0">{sub}</span>
      </div>
      <div className={`font-heading text-2xl font-bold tracking-tight mt-1 ${c.text}`}>{count}</div>
    </div>
  );
}

const AGE_DOT_COLOR = {
  fresh: "bg-emerald-500",
  aging: "bg-[var(--brand-accent)]",
  stale: "bg-orange-500",
  cold: "bg-red-500",
};
const AGE_PILL_COLOR = {
  fresh: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30",
  aging: "text-[var(--brand-accent)] border-amber-900/50 bg-amber-950/30",
  stale: "text-orange-400 border-orange-900/50 bg-orange-950/30",
  cold:  "text-red-400 border-red-900/50 bg-red-950/30",
};

function ageLabel(days) {
  if (days === null || days === undefined) return null;
  if (days < 1) return "today";
  const d = Math.round(days);
  return `${d}d`;
}

function PendingRow({ p, onOpen }) {
  const [copied, setCopied] = useState(false);
  const bucket = p.age_bucket;
  const dot = bucket ? AGE_DOT_COLOR[bucket] : "bg-zinc-700";
  const pill = bucket ? AGE_PILL_COLOR[bucket] : "text-zinc-500 border-zinc-800";
  const ageStr = ageLabel(p.days_pending);

  const copyNudge = async (e) => {
    e.stopPropagation();
    if (!p.share_url) return;
    const msg = `Hey! Just bumping this one — would love your thoughts when you get a chance:\n${p.share_url}`;
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this nudge message:", msg);
    }
  };

  return (
    <li>
      <div className="group w-full flex items-center gap-3 px-3 py-2 bg-black/30 border border-zinc-800/60 rounded-lg hover:border-[var(--brand-accent)]/40 transition">
        <button onClick={onOpen} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <div className="w-20 h-12 shrink-0 rounded overflow-hidden bg-zinc-900 relative">
            {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => {
              healAssetThumbnail(p.id, (fresh) => { e.target.src = fresh; e.target.style.display = ""; });
              e.target.style.display = "none";
            }} /> : <div className="absolute inset-0 flex items-center justify-center text-zinc-700"><Film className="w-4 h-4" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} aria-hidden="true" />
              <span className="text-sm font-medium text-zinc-100 truncate">{p.title}</span>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Awaiting client decision</div>
          </div>
        </button>
        {ageStr && (
          <span
            data-testid={`age-pill-${p.id}`}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border tabular-nums ${pill}`}
            title={p.share_created_at ? `Sent ${new Date(p.share_created_at).toLocaleDateString()}` : ""}
          >
            {ageStr}
          </span>
        )}
        {p.share_url && (
          <button
            onClick={copyNudge}
            data-testid={`copy-nudge-btn-${p.id}`}
            className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border transition flex items-center gap-1 ${copied ? "text-emerald-400 border-emerald-900/50 bg-emerald-950/30" : "text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/40 hover:text-[var(--brand-accent)]"}`}
            title="Copy a friendly nudge message + link to clipboard"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Nudge"}
          </button>
        )}
        {p.duration_sec > 0 && <div className="text-[10px] text-zinc-500 tabular-nums shrink-0 hidden md:block">{fmtTs(p.duration_sec)}</div>}
        <ArrowRight className="w-3.5 h-3.5 text-zinc-600 shrink-0 hidden md:block" />
      </div>
    </li>
  );
}
